'use client';

// The two headers `Modal` draws on its chrome path, lifted out of Modal.tsx so
// that file stays under the 500-line limit: the white `PanelHeader` on the card
// (BL-2305, BL-2692) and the phone sheet's panel with its `ModalHead` (BL-2405).

import type { ReactNode } from 'react';
import { clsx } from 'clsx';
import ModalHead from './ModalHead';
import PanelHeader, { type PanelHeaderDensity } from './PanelHeader';
import { headerWidthForSize, type AdminModalSize } from './modal-sizes';

/** The Modal header props `chromeHeader` reads, plus the dialog's title id. */
export interface ChromeHeaderProps {
    title: string;
    titleId: string;
    subtitle?: ReactNode;
    headerDensity?: PanelHeaderDensity;
    headerThumbnail?: ReactNode;
    headerMetadata?: ReactNode;
    headerActions?: ReactNode;
    headerCloseDisabled?: boolean;
    /** The Modal's width; it picks the title step (BL-2915). */
    size?: AdminModalSize;
    onClose: () => void;
}

/**
 * The chrome path's phone sheet (BL-2405): the settings-v2 sheet panel with the
 * `ModalHead` (Cancel, no ✕), and the caller's children, which bring their own
 * `.ui-modal__body` and `SheetActionBar` on the sheet.
 *
 * @param props.title - The header title (it fades in once a `SheetIntro` scrolls away).
 * @param props.onClose - The header's Cancel.
 * @param props.closeDisabled - Locks the Cancel while the caller's write is in flight, as `headerCloseDisabled` locks the card's ✕ (BL-2819).
 * @param props.className - Extra panel classes from the caller.
 * @param props.children - The Modal's children.
 * @returns The sheet panel.
 */
export function SheetChromePanel(props: { title: string; onClose: () => void; closeDisabled?: boolean; className?: string; children: ReactNode }) {
    const { title, onClose, closeDisabled, className, children } = props;
    return (
        <div className="s-v2" style={{ display: 'contents' }}>
            <div className={clsx('ui-modal ui-modal--sheet', className)} onClick={(e) => e.stopPropagation()}>
                <ModalHead title={title} onClose={onClose} closeDisabled={closeDisabled} />
                {children}
            </div>
        </div>
    );
}

/**
 * The chrome path's white header (BL-2305): `PanelHeader` fed the Modal's
 * header props, its heading carrying the id the dialog is labelled by, and its
 * title step following the Modal's width (BL-2915).
 *
 * @param props - The Modal's title and header props, plus the dialog's title id.
 * @returns The header node.
 */
export function chromeHeader(props: ChromeHeaderProps): ReactNode {
    return (
        <PanelHeader
            title={props.title}
            titleId={props.titleId}
            subtitle={props.subtitle}
            density={props.headerDensity}
            width={headerWidthForSize(props.size)}
            thumbnail={props.headerThumbnail}
            metadata={props.headerMetadata}
            actions={props.headerActions}
            onClose={props.onClose}
            closeDisabled={props.headerCloseDisabled}
        />
    );
}
